import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Table,
  PageHeader,
  Card,
  Input,
  Row,
  Col,
  Icon,
  Tag,
  Select,
  Divider,
} from 'antd';
import { get } from 'lodash';
import {
  fetchAllHubs as fetchAllHubsAction,
  stopMigrationPolling as stopMigrationPollingAction,
  clearFailedMigration as clearFailedMigrationAction,
} from './actions';
import Content from '../../component/Content';
import IntegrationModal from './IntegrationModal';
import s from './index.module.sass';

const { Search } = Input;
const { Option } = Select;

const PathwayUIntegration = ({
  hubs,
  loading,
  clearLoading,
  fetchAllHubs,
  stopMigrationPolling,
  clearFailedMigration,
}) => {
  const [hubSearch, setHubSearch] = useState('');
  const [integrationFilter, setIntegrationFilter] = useState('all');
  const [selectedHub, setSelectedHub] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    fetchAllHubs();

    // Cleanup: stop polling when component unmounts
    return () => {
      stopMigrationPolling();
    };
  }, [fetchAllHubs, stopMigrationPolling]);

  const handleSearch = value => {
    setHubSearch(value);
  };

  const handleFilterChange = value => {
    setIntegrationFilter(value);
  };

  const handleIntegratePathwayU = hub => {
    setSelectedHub(hub);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedHub(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchAllHubs();
  };

  const handleRetryMigration = async hub => {
    try {
      // Clear the failed migration first
      await clearFailedMigration(hub.id);
      // Then open the modal for fresh migration
      setSelectedHub(hub);
      setIsModalVisible(true);
    } catch (error) {
      // If clearing fails, still open the modal but show error
      setSelectedHub(hub);
      setIsModalVisible(true);
    }
  };

  const filteredHubs = useMemo(() => {
    let filtered = hubs || [];

    // Ensure all hubs have valid data
    filtered = filtered.filter(hub => hub && typeof hub === 'object');

    // Filter by search term
    if (hubSearch) {
      filtered = filtered.filter(
        hub =>
          hub.name?.toLowerCase().includes(hubSearch.toLowerCase()) ||
          hub.identifier?.toLowerCase().includes(hubSearch.toLowerCase())
      );
    }

    // Filter by integration status
    if (integrationFilter !== 'all') {
      filtered = filtered.filter(hub => {
        const isIntegrated = hub.pathwayUIntegration?.enabled;
        if (integrationFilter === 'integrated') {
          return isIntegrated;
        }
        if (integrationFilter === 'not-integrated') {
          return !isIntegrated;
        }
        return true;
      });
    }

    // Sort alphabetically by hub name
    filtered.sort((a, b) => a.name?.localeCompare(b.name));

    return filtered;
  }, [hubs, hubSearch, integrationFilter]);

  const renderIntegrationStatus = hub => {
    if (!hub) return null;

    const isIntegrated = hub.pathwayUIntegration?.enabled;
    const migrationStatus = hub.pathwayUMigration;

    // Show migration status if there's an active migration
    if (migrationStatus) {
      if (migrationStatus.status === 'failed') {
        return (
          <div>
            <Tag color="red">
              <Icon type="close-circle" /> Migration Failed
            </Tag>
            {migrationStatus.errorMessage && (
              <div
                style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '4px' }}
              >
                {String(migrationStatus.errorMessage)}
              </div>
            )}
          </div>
        );
      }

      if (migrationStatus.status === 'completed') {
        return (
          <Tag color="green">
            <Icon type="check-circle" /> Migration Completed
          </Tag>
        );
      }

      return (
        <Tag color="blue">
          <Icon type="loading" spin /> Migration in Progress
        </Tag>
      );
    }

    if (isIntegrated) {
      return (
        <Tag color="green">
          <Icon type="check-circle" /> Integrated
        </Tag>
      );
    }

    return (
      <Tag color="red">
        <Icon type="close-circle" /> Not Integrated
      </Tag>
    );
  };

  const renderActions = hub => {
    if (!hub) return null;

    const isIntegrated = hub.pathwayUIntegration?.enabled;
    const migrationStatus = hub.pathwayUMigration;

    // Show migration status if there's an active migration
    if (migrationStatus) {
      if (migrationStatus.status === 'failed') {
        return (
          <>
            <Button
              type="default"
              size="small"
              disabled
              aria-label={`PathwayU migration failed for ${hub.name}`}
            >
              Migration Failed
            </Button>
            <Divider type="vertical" />
            <Button
              type="default"
              size="small"
              loading={clearLoading}
              aria-label={`Retry PathwayU migration for ${hub.name}`}
              onClick={() => handleRetryMigration(hub)}
            >
              Retry Migration
            </Button>
          </>
        );
      }

      if (migrationStatus.status === 'completed') {
        return (
          <Button
            type="default"
            size="small"
            disabled
            aria-label={`PathwayU migration completed for ${hub.name}`}
          >
            Migration Completed
          </Button>
        );
      }

      return (
        <Button
          type="default"
          size="small"
          disabled
          aria-label={`PathwayU migration in progress for ${hub.name}`}
        >
          <Icon type="loading" spin /> Migrating...
        </Button>
      );
    }

    if (isIntegrated) {
      return (
        <Button
          type="default"
          size="small"
          disabled
          aria-label={`PathwayU already integrated for ${hub.name}`}
        >
          Already Integrated
        </Button>
      );
    }

    return (
      <Button
        type="primary"
        size="small"
        onClick={() => handleIntegratePathwayU(hub)}
        aria-label={`Integrate PathwayU for ${hub.name}`}
      >
        Integrate PathwayU
      </Button>
    );
  };

  const columns = [
    {
      title: 'Hub Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name?.localeCompare(b.name),
      render: (text, record) => (
        <div>
          <div className={s.hubName}>
            {text} [{record.id}]
          </div>
          <div className={s.hubIdentifier}>{record.identifier}</div>
        </div>
      ),
    },
    {
      title: 'Integration Status',
      key: 'integrationStatus',
      render: (_, record) => renderIntegrationStatus(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => renderActions(record),
    },
  ];

  return (
    <Content>
      <PageHeader
        title="PathwayU Integration"
        subTitle="Manage PathwayU integrations for hubs"
      />

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Search
              placeholder="Search hubs by name or identifier"
              onChange={e => handleSearch(e.target.value)}
              style={{ width: '100%' }}
              aria-label="Search hubs"
            />
          </Col>
          <Col span={12}>
            <Select
              defaultValue="all"
              style={{ width: '100%' }}
              onChange={handleFilterChange}
              aria-label="Filter by integration status"
            >
              <Option value="all">All Hubs</Option>
              <Option value="integrated">PG Integrated PathwayU</Option>
              <Option value="not-integrated">Not Integrated</Option>
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredHubs}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} hubs`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {selectedHub && isModalVisible && (
        <IntegrationModal
          visible={isModalVisible}
          hub={selectedHub}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </Content>
  );
};

const mapStateToProps = state => ({
  hubs: get(state, 'pathwayuIntegration.hubs', []),
  loading: get(state, 'pathwayuIntegration.loading', false),
  clearLoading: get(state, 'pathwayuIntegration.clearLoading', false),
});

const mapDispatchToProps = {
  fetchAllHubs: fetchAllHubsAction,
  stopMigrationPolling: stopMigrationPollingAction,
  clearFailedMigration: clearFailedMigrationAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PathwayUIntegration);
